import { Type } from 'class-transformer';
import type dayjs from 'dayjs';
import { Model } from './model';
import { Email } from './email';
import { Partner } from './partner';
import { Task } from './task';
import { CreditMemo } from './credit_memo';
import { Invoice } from './invoice';
import { OutreachWorkflow } from './outreach_workflow';
import { EmailStatus } from './email';
import { TransformStringToDayJS } from './utils';

export type EmailThreadTriageStatus = 'pending' | 'auto_matched' | 'manually_matched' | 'dismissed';
export type MatchSource = 'llm' | 'manual' | 'auto';

/** Lightweight suggestion row from triage / agent (not a full Partner model). */
export type EmailThreadSuggestedPartner = {
  partner_id: string;
  partner_name: string;
  reason: string;
};

export type SenderContactInfo = {
  given_name: string | null;
  family_name: string | null;
  email: string | null;
  job_title: string | null;
  phone: string | null;
};

export class EmailThreadTriage extends Model {
  status: EmailThreadTriageStatus;

  suggested_partner_id: string | null;

  @Type(() => Partner)
  suggested_partner?: Partner | null;

  suggested_partners: EmailThreadSuggestedPartner[] | null;

  agent_confidence_score: number | null;

  agent_confidence_reason: string | null;

  match_evidence: Record<string, any>[] | null;

  match_source: MatchSource | null;

  agent_error_code: string | null;

  @TransformStringToDayJS()
  matched_at: dayjs.Dayjs | null;

  matched_by_id: string | null;

  sender_contact_info?: SenderContactInfo | null;
}

export class EmailThread extends Model {
  id: string;

  @Type(() => Email)
  emails: Email[];

  @Type(() => Invoice)
  invoices: Invoice[];

  @Type(() => CreditMemo)
  credit_memos: CreditMemo[];

  num_drafts: number;

  num_unread: number;

  workflow: OutreachWorkflow;

  @Type(() => Partner)
  partner?: Partner | null;

  partner_id: string | null;

  @Type(() => Task)
  tasks: Task[] | null;

  is_from_centralized_inbox: boolean = false;

  is_internal: boolean = false;

  @Type(() => EmailThreadTriage)
  triage?: EmailThreadTriage | null;

  processing_status?: string;

  get needsTriage(): boolean {
    return this.is_from_centralized_inbox && this.triage?.status === 'pending';
  }

  get isProcessing(): boolean {
    return (
      this.is_from_centralized_inbox &&
      this.triage?.status === 'pending' &&
      this.triage?.match_source === null &&
      this.triage?.agent_error_code === null
    );
  }

  get taskProcessingLabel() {
    return this.processing_status !== 'completed' && this.processing_status;
  }

  get hasSuggestion(): boolean {
    return !!this.triage?.suggested_partner || !!this.triage?.suggested_partner_id;
  }

  get has_drafts(): boolean {
    return this.num_drafts > 0;
  }

  get has_unread(): boolean {
    return this.num_unread > 0;
  }

  get firstEmail(): Email {
    return this.emails[0];
  }

  get latestEmail(): Email {
    return this.emails[this.emails.length - 1];
  }

  get latestOpenEmail(): Email {
    const nonDraftEmails = this.emails?.filter((em) => !em.isDraft);
    return nonDraftEmails?.[nonDraftEmails?.length - 1];
  }

  get snippet(): string | null {
    return this.latestOpenEmail?.snippet;
  }

  get subject(): string {
    return this.latestEmail?.subject;
  }

  get from(): string | null {
    return this.latestEmail?.from_email;
  }

  get to(): string {
    return this.latestEmail?.to[0];
  }

  get toParticipants(): string[] {
    const emailSenderEmail = this.firstEmail?.email_sender?.email;
    return this.getParticipants()?.filter((participant) => participant !== emailSenderEmail);
  }

  get hasAttachments(): boolean {
    return this.emails.some((email) => email.attachments?.length);
  }

  get attachmentCount(): number {
    return this.emails.reduce((count, email) => count + (email.attachments?.length || 0), 0);
  }

  getEmailById(emailId: string): Email | null {
    for (const email of this.emails) {
      if (email.id === emailId) return email;
    }

    return null;
  }

  getParticipants(): string[] {
    const participants = new Set<string>();
    for (const email of this.emails) {
      if (email.status === EmailStatus.SENT) {
        for (const participant of email.getParticipants()) {
          participants.add(participant);
        }
      }
    }
    return Array.from(participants);
  }

  get isDraft() {
    return this.emails?.every((em) => em.isDraft);
  }

  get invoicesWithPdfIds() {
    return this.invoices?.filter((inv) => inv.pdf_id) || [];
  }

  get openTasks() {
    return this.tasks?.filter((task) => task.isOpen);
  }

  get completedTasks() {
    return this.tasks?.filter((task) => task.isCompleted);
  }

  get closedTasks() {
    return this.tasks?.filter((task) => task.isClosed);
  }

  get openResponseTasks() {
    return this.openTasks?.filter((task) => task.isReplyRequired);
  }

  get oldestOpenResponseTask() {
    return this.openResponseTasks?.toSorted((a, b) => {
      if (!a || !b) return 0;
      return a.created_at.diff(b.created_at);
    })[0];
  }
}
